// FundraisingSection.js
// Extracted from MemorialPage.js - Complete fundraising functionality
// Includes: Donation summary display, donation modal, QR code sharing, and donation list

import React, { useEffect, useState } from 'react';
import { isInNativeApp } from './platform';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc 
} from 'firebase/firestore';

// Helper function to capitalize words
const capitalizeWords = (words) => {
  if (!words) return '';
  return words
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get user display name
const getUserDisplayName = (user) => {
  if (!user) return 'Anonymous';
  return user.displayName || user.email?.split('@')[0] || 'Anonymous';
};

export default function FundraisingSection({
  memorial,
  memorialId,
  slug,
  auth,
  db,
  // Subscription-related props for determining if donations are enabled
  creatorSubscription,
  checkingSubscription,
  isSubscriptionActive,
  // Modal state props (controlled by parent for action bar button)
  showDonateQR,
  setShowDonateQR,
  activeTab,
  setActiveTab,
}) {
  // Donation form state variables (internal to this component)
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donationMessage, setDonationMessage] = useState('');
  const [donations, setDonations] = useState([]);
  const [isAnonymousDonation, setIsAnonymousDonation] = useState(false);

  // Check if donations are enabled based on creator's subscription
  const isDonationsEnabled = () => {
    if (checkingSubscription) return false; // Don't show donations while checking
    
    // Check creator subscription
    const hasActiveSubscription = creatorSubscription && isSubscriptionActive(creatorSubscription);
    if (!hasActiveSubscription) return false;
    
    // Check memorial's donationEnabled field (default to true if not present)
    const memorialDonationsEnabled = memorial?.donationEnabled !== undefined 
      ? memorial.donationEnabled 
      : true; // Default to true for backward compatibility
    
    return memorialDonationsEnabled;
  };

  // Donation helper function
  const getDonationAmount = () => {
    if (selectedAmount) return selectedAmount;
    if (customAmount && !isNaN(customAmount)) return parseFloat(customAmount);
    return 0;
  };

  // Complete donation handling function
  const handleDonation = async () => {
    const amount = getDonationAmount();
    if (amount < 5) {
      alert('Minimum donation is $5');
      return;
    }

    // Check if donations are enabled
    if (!isDonationsEnabled()) {
      alert('Donations are not available for this memorial.');
      return;
    }

    if (!window.Stripe) {
      alert('Payment system not loaded. Please refresh and try again.');
      return;
    }

    
    
    try {
      const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
      
      const platformFee = amount * 0.03; // 3% platform fee
      const familyAmount = amount * 0.97; // 97% to family
      
      // Use your existing createDonationSession endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/createDonationSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memorialId: memorialId, // Keep sending Firebase document ID for backend processing
          memorialSlug: slug, // Also send the slug for the success URL
          amount: amount,
          message: donationMessage.trim(),
          donorName: auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Anonymous',
          donorEmail: auth.currentUser?.email || 'anonymous@donation.com',
          platformFee: platformFee,
          familyAmount: familyAmount
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create donation session');
      }
      
      const { sessionId } = await response.json();
      
      if (!sessionId) {
        throw new Error('No session ID received from server');
      }
      
      // Store donation data temporarily before redirect
      sessionStorage.setItem('pendingDonation', JSON.stringify({
        amount: amount,
        message: donationMessage.trim(),
        donorName: auth.currentUser ? getUserDisplayName(auth.currentUser) : 'Anonymous',
        donorEmail: auth.currentUser?.email || 'anonymous@donation.com',
        platformFee: platformFee,
        familyAmount: familyAmount,
        memorialId: memorialId,
        timestamp: new Date().toISOString(),
        isAnonymous: isAnonymousDonation // Include anonymous flag
      }));
      
      
      
      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({ sessionId });
      
      if (error) {
        console.error('Stripe redirect error:', error);
        sessionStorage.removeItem('pendingDonation');
        alert(`Payment failed: ${error.message}`);
      }
      
    } catch (error) {
      console.error('❌ Donation error:', error);
      alert(`Error: ${error.message}`);
      // Clear any pending data on error
      sessionStorage.removeItem('pendingDonation');
    }
  };

  // Subscribe to donations only if enabled
  useEffect(() => {
    if (!memorialId || !isDonationsEnabled()) return;
    
    const q = query(
      collection(db, 'memorials', memorialId, 'donations'),
      orderBy('timestamp', 'desc')
    );
    const unsubscribe = onSnapshot(q, snapshot => {
      setDonations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, console.error);
    return () => unsubscribe();
  }, [memorialId, checkingSubscription, creatorSubscription, db]);

  // Handle successful donation with complete verification flow
  useEffect(() => {
    const handleDonationSuccess = async () => {
      const params = new URLSearchParams(window.location.search);
      const donationSessionId = params.get('donation_session_id'); // This matches your backend success URL
      
      
      
      if (donationSessionId && memorialId) {
        
        
        try {
          // Verify the donation payment with your backend
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/verifyDonationPayment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              sessionId: donationSessionId, 
              memorialId: memorialId 
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: Failed to verify donation payment`);
          }

          const data = await response.json();
          
          
          if (data.success) {
            
            
            // Get pending donation data
            const pendingDonation = sessionStorage.getItem('pendingDonation');
            
            if (pendingDonation) {
              const donationData = JSON.parse(pendingDonation);
              
              
              
              // Add donation to Firestore
              await addDoc(collection(db, 'memorials', memorialId, 'donations'), {
                donorName: donationData.isAnonymous ? 'Anonymous' : donationData.donorName,
                donorEmail: donationData.donorEmail,
                amount: donationData.amount,
                message: donationData.message || '',
                isAnonymous: donationData.isAnonymous || false,
                platformFee: donationData.platformFee,
                familyAmount: donationData.familyAmount,
                timestamp: new Date(),
                sessionId: donationSessionId,
                verified: true,
                paid: true,
                userId: auth.currentUser?.uid || 'anonymous',
                // Store verification data
                stripeAmount: data.amount,
                stripeCustomerEmail: data.customerEmail
              });
              
              
              
              // Clear form and pending data
              setSelectedAmount(null);
              setCustomAmount('');
              setDonationMessage('');
              setIsAnonymousDonation(false);
              sessionStorage.removeItem('pendingDonation');
              
              // Show success message
              alert(`Thank you for your $${donationData.amount} donation! 💚 Your contribution will help honor ${capitalizeWords(memorial.prefix)} ${capitalizeWords(memorial.firstName)}'s memory.`);
              
              // Clean up URL immediately after success
              window.history.replaceState({}, document.title, window.location.pathname);
              
            } else {
              
              // Even without pending data, we can show success
              alert(`Thank you for your $${data.amount} donation! 💚`);
              // Clean up URL
              window.history.replaceState({}, document.title, window.location.pathname);
            }
          } else {
            console.error('❌ Donation payment verification failed:', data);
            alert('Donation verification failed. Please contact support if you were charged.');
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error('❌ Error verifying donation payment:', error);
          alert('Error verifying donation payment. Please contact support if you were charged.');
          // Clean up URL
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    };

    // Only run if we have required data
    if (memorialId && memorial) {
      handleDonationSuccess();
    }
  }, [memorialId, memorial, auth, db]);

  // Handle QR code donation link (only if donations are enabled)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shouldDonate = params.get('donate');
    
    if (shouldDonate === 'true' && isDonationsEnabled()) {
      // Open the donation modal directly
      setTimeout(() => {
        setShowDonateQR(true);
        setActiveTab('donate');
      }, 500);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [checkingSubscription, creatorSubscription]);

  // Don't render anything if donations are not enabled
  if (!isDonationsEnabled()) {
    return null;
  }

  return (
    <>
      {/* Donations Summary Section - Squarespace style */}
      <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
              Supporting the Family
            </p>
            <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
              Family's Fundraiser
            </h2>
            <div
              className="mx-auto mt-4 mb-3"
              style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
            />
            <p className="text-xs text-gray-500 font-light italic mb-6">
              All proceeds from the fundraiser will be transferred to this memorial ID owner
            </p>
            
            {memorial && memorial.donationDescription && (
              <div className="bg-[#faf9f7] rounded-2xl border border-[#eee9df] p-6 mb-8">
                <p className="whitespace-pre-line text-base md:text-lg font-light leading-relaxed text-gray-700">
                  {memorial.donationDescription}
                </p>
              </div>
            )}
          </div>
          
          {donations.length > 0 ? (
            <div className="space-y-8">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center p-6 bg-white border border-gray-100">
                  <div className="text-4xl md:text-5xl font-light text-black mb-2">
                    ${donations.reduce((sum, d) => sum + d.amount, 0).toLocaleString()}
                  </div>
                  <div className="text-sm uppercase tracking-wide text-gray-600 font-medium">
                    Total Raised
                  </div>
                  {memorial && memorial.donationGoal && (
                    <div className="mt-4">
                      <div className="w-full h-2 bg-gray-200">
                        <div 
                          className="h-full bg-[#73c354] transition-all duration-500"
                          style={{ 
                            width: `${Math.min(100, (donations.reduce((sum, d) => sum + d.amount, 0) / memorial.donationGoal) * 100)}%` 
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-2 font-light">
                        Goal: ${memorial.donationGoal.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="text-center p-6 bg-white border border-gray-100">
                  <div className="text-4xl md:text-5xl font-light text-black mb-2">
                    {donations.length}
                  </div>
                  <div className="text-sm uppercase tracking-wide text-gray-600 font-medium">
                    Donation{donations.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
              
              {/* Action Button */}
              <div className="text-center">
                <button
                  onClick={() => {
                    setShowDonateQR(true);
                    setActiveTab('donations');
                  }}
                  className="inline-flex items-center px-8 py-3 rounded-full bg-[#274a35] text-[#ccf88e] font-medium text-sm uppercase tracking-wider hover:bg-[#1e3829] transition-colors duration-300"
                >
                  View All Donations
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center space-y-8">
              <div className="space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-[#faf6ec] border border-[#e2cf9f] flex items-center justify-center">
                  <span className="text-2xl">❤️</span>
                </div>
                <div className="max-w-md mx-auto space-y-3">
                  <p className="text-gray-600 font-light leading-relaxed">
                    Family and friends can make donations to honor {capitalizeWords(memorial.firstName)}'s memory and support the family during this time.
                  </p>
                </div>
              </div>
              
              {memorial && memorial.donationGoal && (
                <div className="max-w-sm mx-auto bg-white border border-gray-100 p-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm uppercase tracking-wide text-gray-600 font-medium mb-2">
                        Fundraising Goal
                      </p>
                      <p className="text-3xl font-light text-black">
                        ${memorial.donationGoal.toLocaleString()}
                      </p>
                    </div>
                    
                    <div>
                      <div className="w-full h-2 bg-gray-200">
                        <div className="h-full bg-[#73c354] w-0 transition-all duration-300"></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 font-light">
                        $0 raised so far
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div>
                <button
                  onClick={() => {
                    setShowDonateQR(true);
                    setActiveTab('donate');
                  }}
                  className="sm-btn-primary"
                >
                  <span className="mr-2">❤️</span>
                  Make the First Donation
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Donation QR Modal - Squarespace style with sharp corners */}
      {showDonateQR && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100001] p-2 md:p-4" style={{ fontFamily: 'Garamond, serif' }}>
          <div className="bg-white p-4 md:p-6 max-w-md md:max-w-2xl w-full mx-2 md:mx-4 shadow-2xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto relative">
            {/* Close button in top right */}
            <button
              onClick={() => {
                setShowDonateQR(false);
                setSelectedAmount(null);
                setCustomAmount('');
                setDonationMessage('');
                setIsAnonymousDonation(false);
                setActiveTab('donate');
              }}
              className="absolute top-2 right-2 md:top-4 md:right-4 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all duration-200 z-10"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-4 md:mb-6">
              <h3 className="text-xl md:text-3xl font-light text-gray-800 mb-2">
                {memorial.donationTitle?.trim() 
                  ? capitalizeWords(memorial.donationTitle) 
                  : `Honor ${capitalizeWords(memorial.firstName)}'s Memory`
                }
              </h3>
              {/* Show donation goal if available */}
              {memorial && memorial.donationGoal && (
                <div className="p-4 mb-3 md:mb-4 rounded-xl border" style={{ backgroundColor: '#f2f7f0', borderColor: '#d3e3cd' }}>
                  <p className="text-xs md:text-sm text-black">
                    <strong>Fundraising Goal:</strong> ${memorial.donationGoal.toLocaleString()}
                  </p>
                  <div className="mt-2">
                    <div className="w-full h-2 md:h-3 bg-white rounded-full overflow-hidden border border-[#d3e3cd]">
                      <div 
                        className="h-full rounded-full transition-all duration-300"
                        style={{ 
                          width: `${Math.min(100, (donations.reduce((sum, d) => sum + d.amount, 0) / memorial.donationGoal) * 100)}%`,
                          background: 'linear-gradient(90deg, #73c354, #a8d879)'
                        }}
                      />
                    </div>
                    <p className="text-xs text-black mt-1">
                      ${donations.reduce((sum, d) => sum + d.amount, 0).toLocaleString()} raised so far
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs - Squarespace style */}
            <div className="flex bg-[#f3f1ec] rounded-full p-1 mb-4 md:mb-6 gap-1">
              <button
                onClick={() => setActiveTab('donate')}
                className={`flex-1 py-2 md:py-3 rounded-full font-medium text-xs md:text-sm transition-all duration-300 ${
                  activeTab === 'donate'
                    ? 'text-white shadow-lg'
                    : 'text-gray-600 hover:bg-white hover:shadow-sm'
                }`}
                style={activeTab === 'donate' ? { backgroundColor: '#274a34' } : {}}
              >
                ❤️ Donate Now
              </button>
              <button
                onClick={() => setActiveTab('qr')}
                className={`flex-1 py-2 md:py-3 rounded-full font-medium text-xs md:text-sm transition-all duration-300 ${
                  activeTab === 'qr'
                    ? 'text-white shadow-lg'
                    : 'text-gray-600 hover:bg-white hover:shadow-sm'
                }`}
                style={activeTab === 'qr' ? { backgroundColor: '#274a34' } : {}}
              >
                📱 Share QR
              </button>
              <button
                onClick={() => setActiveTab('donations')}
                className={`flex-1 py-2 md:py-3 rounded-full font-medium text-xs md:text-sm transition-all duration-300 ${
                  activeTab === 'donations'
                    ? 'text-white shadow-lg'
                    : 'text-gray-600 hover:bg-white hover:shadow-sm'
                }`}
                style={activeTab === 'donations' ? { backgroundColor: '#274a34' } : {}}
              >
                💝 Recent ({donations.length})
              </button>
            </div>

            {activeTab === 'donate' && (
              <div>
                {/* Donation Amount Buttons - Squarespace style */}
                <div className="grid grid-cols-2 gap-2 md:gap-3 mb-3 md:mb-4">
                  {[25, 50, 100, 250].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setSelectedAmount(amount)}
                      className={`p-2 md:p-3 font-medium text-sm md:text-base transition duration-300 ${
                        selectedAmount === amount
                          ? 'sm-amount-active'
                          : 'sm-amount'
                      }`}
                    >
                      ${amount}
                    </button>
                  ))}
                </div>
                
                {/* Custom Amount */}
                <div className="mb-3 md:mb-4">
                  <label className="block text-gray-700 font-medium mb-2 text-xs md:text-sm">Custom Amount</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm md:text-base">$</span>
                    <input
                      type="number"
                      min="5"
                      placeholder="Enter amount"
                      value={customAmount}
                      onChange={e => {
                        setCustomAmount(e.target.value);
                        setSelectedAmount(null);
                      }}
                      className="w-full pl-7 pr-4 py-2 md:py-3 bg-[#faf9f7] border border-[#eae6dd] rounded-xl focus:outline-none focus:border-[#cbab6a] text-sm md:text-base transition-colors"
                    />
                  </div>
                </div>

                {/* Anonymous Donation Checkbox */}
                <div className="mb-3 md:mb-4">
                  <label className="flex items-center gap-2 text-xs md:text-sm">
                    <input
                      type="checkbox"
                      checked={isAnonymousDonation}
                      onChange={(e) => setIsAnonymousDonation(e.target.checked)}
                      className="border-gray-300 text-black focus:ring-black"
                    />
                    <span className="text-gray-700">Make this donation anonymous</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    Anonymous donations will show as "Anonymous" in the public donation list
                  </p>
                </div>

                {/* Donation Message */}
                <div className="mb-3 md:mb-4">
                  <label className="block text-gray-700 font-medium mb-2 text-xs md:text-sm">Message (Optional)</label>
                  <textarea
                    rows={2}
                    placeholder="Leave a message with your donation..."
                    value={donationMessage}
                    onChange={e => setDonationMessage(e.target.value)}
                    className="w-full px-3 md:px-4 py-2 md:py-3 bg-[#faf9f7] border border-[#eae6dd] rounded-xl focus:outline-none focus:border-[#cbab6a] resize-none text-xs md:text-sm transition-colors"
                  />
                </div>

                {/* Fee Breakdown */}
                {getDonationAmount() > 0 && (
                  <div className="p-3 md:p-4 mb-3 md:mb-4 text-xs md:text-sm" style={{ backgroundColor: '#e7f1e7' }}>
                    <div className="flex justify-between mb-1">
                      <span>Donation Amount:</span>
                      <span className="font-medium">${getDonationAmount()}</span>
                    </div>
                    <div className="flex justify-between mb-1 text-gray-600">
                      <span>Processing Fee (3%):</span>
                      <span>${(getDonationAmount() * 0.03).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-medium pt-2 border-t border-gray-400">
                      <span>To Family:</span>
                      <span className="text-black">${(getDonationAmount() * 0.97).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Donate Button — hidden in the native app (App Store 3.1.1) */}
                {isInNativeApp() ? (
                  <p className="text-center text-xs text-gray-500 leading-relaxed py-3 mb-3 md:mb-4">
                    Donations are made on the web — visit soulemap.com from any browser.
                  </p>
                ) : (
                <button
                  onClick={handleDonation}
                  disabled={getDonationAmount() < 5}
                  className="w-full text-white py-3 md:py-4 font-medium text-sm md:text-base rounded-full shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5 transition duration-300 mb-3 md:mb-4"
                  style={{ backgroundColor: '#274a34' }}
                >
                  ❤️ Donate ${getDonationAmount() || 0}
                </button>
                )}
              </div>
            )}

            {activeTab === 'qr' && (
              <div className="text-center">
                <p className="text-gray-600 mb-3 md:mb-4 text-xs md:text-sm">
                  Share this QR code to let others donate
                </p>
                
                <div className="bg-[#faf9f7] rounded-2xl border border-[#eee9df] p-4 md:p-6 mb-3 md:mb-4 inline-block">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                      `${window.location.origin}/memorial/${slug}?donate=true`
                    )}`}
                    alt="Donation QR Code"
                    className="w-[150px] h-[150px] md:w-[180px] md:h-[180px]"
                  />
                </div>
                
                <div className="p-4 mb-3 md:mb-4 rounded-xl border" style={{ backgroundColor: '#f2f7f0', borderColor: '#d3e3cd' }}>
                  <p className="text-xs text-black break-all mb-2">
                    {window.location.origin}/memorial/{slug}?donate=true
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(`${window.location.origin}/memorial/${slug}?donate=true`);
                        alert('Donation link copied!');
                      } catch {
                        alert('Failed to copy link.');
                      }
                    }}
                    className="sm-btn-small"
                  >
                    📋 Copy Link
                  </button>
                </div>

                <div className="text-left bg-[#faf9f7] rounded-xl border border-[#eee9df] p-3 text-xs text-gray-600">
                  <strong>How to use:</strong><br/>
                  • Save QR code to share on social media<br/>
                  • Include in funeral programs or emails<br/>
                  • 97% of donations go directly to family
                </div>
              </div>
            )}

            {activeTab === 'donations' && (
              <div>
                {donations.length > 0 ? (
                  <div className="space-y-2 md:space-y-3 max-h-48 md:max-h-60 overflow-y-auto">
                    {donations.slice(0, 10).map((donation, idx) => (
                      <div
                        key={donation.id || idx}
                        className="p-3 md:p-4 rounded-xl border"
                        style={{ backgroundColor: '#f2f7f0', borderColor: '#d3e3cd' }}
                      >
                        <div className="flex items-center justify-between mb-1 md:mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-light" style={{ background: 'radial-gradient(circle at 35% 30%, #2a3152, #0d1020)', color: '#ffe2a8' }}>
                              {(donation.isAnonymous ? 'A' : donation.donorName?.charAt(0).toUpperCase()) || 'A'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 text-xs md:text-sm">
                                {donation.isAnonymous ? 'Anonymous' : donation.donorName}
                              </p>
                              <p className="text-xs text-gray-500">
                                {donation.timestamp?.toDate?.() 
                                  ? donation.timestamp.toDate().toLocaleDateString()
                                  : new Date(donation.timestamp).toLocaleDateString()
                                }
                              </p>
                            </div>
                          </div>
                          <p className="font-semibold text-black text-sm md:text-base">${donation.amount}</p>
                        </div>
                        {donation.message && (
                          <p className="text-gray-700 text-xs bg-white/70 rounded-lg p-2 mt-1">{donation.message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 md:py-8">
                    <div className="text-3xl md:text-4xl mb-2 md:mb-3">❤️</div>
                    <p className="text-gray-500 text-xs md:text-sm">No donations yet</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
